import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RequestsService } from '@api/services/requests.service';
import {
  RequestDeal,
  RequestDetail,
  RequestEvent,
  Inkoopkans,
} from '@api/models/request.model';
import { CardComponent } from '@ui/card/card.component';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '@ui/app-layout/loading-service';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { ButtonComponent } from '@ui/button/button.component';
import { RouterLink } from '@angular/router';
import { ModalService } from '@ui/modal/modal.service';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { CreateRequestModalComponent } from '../create-request-modal/create-request-modal.component';
import { EditDealModalComponent } from '../edit-deal-modal/edit-deal-modal.component';
import { RequestsDocumentsComponent } from '../requests-documents/requests-documents.component';
import {
  TimelineComponent,
  TimelineItem,
} from '@ui/timeline/timeline.component';
import { RequestsDealsComponent } from '../requests-deals/requests-deals.component';
import { AppService } from '../../../app.service';

@Component({
  selector: 'app-requests-detail',
  standalone: true,
  imports: [
    CardComponent,
    CommonModule,
    ButtonComponent,
    RouterLink,
    RequestsDocumentsComponent,
    TimelineComponent,
    RequestsDealsComponent,
    TitleCasePipe,
  ],
  template: `<div class="flex flex-col gap-4 pb-4">
    <ui-card>
      <div class="flex justify-between items-center">
        <div class="flex items-center gap-2">
          <ui-button
            [routerLink]="[listPath]"
            [icon]="'arrow-back'"
            [variant]="'ghost'"
          ></ui-button>
          <h1>{{ request()?.wmo_reference }}</h1>
        </div>
        <div class="flex items-center gap-2">
          @if (!isClosed()) {
          <ui-button
            [icon]="'success'"
            [variant]="'success'"
            (click)="closeRequest()"
            >Afronden</ui-button
          >
          <ui-button
            [icon]="'cancel'"
            [variant]="'danger'"
            (click)="cancelRequest()"
            >Annuleer aanvraag</ui-button
          >
          @if (canEditRequest()) {
          <ui-button
            [icon]="'edit'"
            [variant]="'primary'"
            (click)="editRequest()"
            >Aanvraag bewerken</ui-button
          >
          }
          }
          @if (canEditDeal()) {
          <ui-button [icon]="'edit'" (click)="editDeal()"
            >Bewerken</ui-button
          >
          } @if (isWaitingForSync()) {
          <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3" style="margin-left: 1rem;">
            <span class="text-sm text-gray-500" style="padding-left: 0.5rem; padding-right: 0.5rem;">
              Bezig met synchroniseren... Ververs de pagina
            </span>
            <ui-button [icon]="'edit'" [disabled]="true"
              >Bewerken</ui-button
            >
          </div>
          }
          @if (hasAssignment()) {
          <ui-button
            [icon]="'assignments'"
            [variant]="'ghost'"
            (click)="goToAssignment()"
            >Ga naar opdracht</ui-button
          >
          }
        </div>
      </div>
    </ui-card>

    <div class="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
      <!-- Left Column -->
      <div class="flex flex-col gap-4">
        <!-- Request Details Card -->
        <ui-card
          class="h-full"
          [title]="'Aanvraag Details'"
          [loading]="loading()"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="grid grid-cols-[150px_1fr] gap-y-3 text-sm">
              <span class="text-gray-600">WMO referentie:</span>
              <span class="font-medium">{{ request()?.wmo_reference }}</span>

              @if(request()?.type === 'request') {
              <span class="text-gray-600">WMO SO referentie:</span>
              <span class="font-medium">{{
                soReference()
              }}</span>
              }

              <span class="text-gray-600">Factuur referentie:</span>
              <span class="font-medium">{{
                factuurReference()
              }}</span>

              <span class="text-gray-600">Status:</span>
              <span class="font-medium">{{
                request()?.status?.status | titlecase
              }}</span>

              <span class="text-gray-600">Type:</span>
              <span class="font-medium">{{
                getTypeLabel(request()?.type)
              }}</span>

              <span class="text-gray-600">Aangemaakt op:</span>
              <span class="font-medium">{{
                request()?.created_at | date : 'dd-MM-yyyy HH:mm'
              }}</span>

              <span class="text-gray-600">Laatst aangepast op:</span>
              <span class="font-medium">{{
                request()?.updated_at | date : 'dd-MM-yyyy HH:mm'
              }}</span>
            </div>
            <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
              @if(request()?.type === 'request') {
              <span class="text-gray-600">Beschrijving:</span>
              <div
                class="font-medium border rounded-lg p-3 max-h-[145px] overflow-y-auto whitespace-pre-line"
              >
                {{ request()?.details?.description }}
              </div>
              } @if(request()?.type === 'withdrawal') {
              <span class="text-gray-600">Opmerkingen:</span>
              <div
                class="font-medium border rounded-lg p-3 max-h-[145px] overflow-y-auto whitespace-pre-line"
              >
                {{ request()?.task?.description }}
              </div>
              }
            </div>
          </div>
        </ui-card>

        <!-- Customer Info Card -->
        <ui-card class="h-full" [title]="'Klant'" [loading]="loading()">
          <div class="flex flex-row gap-10">
            <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
              <span class="text-gray-600">Naam:</span>
              <span class="font-medium"
                >{{ request()?.details?.first_name }}
                {{ request()?.details?.last_name }}</span
              >

              <span class="text-gray-600">Adres:</span>
              <span class="font-medium">
                {{ request()?.details?.street }}
                {{ request()?.details?.housenumber }}
                {{ request()?.details?.housenumber_addon }},
                {{ request()?.details?.postal_code }}
                {{ request()?.details?.city }}
              </span>

              <span class="text-gray-600">Telefoon:</span>
              <span class="font-medium">{{ request()?.details?.phone }}</span>

              <span class="text-gray-600">Email:</span>
              <span class="font-medium">{{ request()?.details?.email }}</span>

              <span class="text-gray-600">Geboortedatum:</span>
              <span class="font-medium">{{
                request()?.details?.date_of_birth | date : 'dd-MM-yyyy'
              }}</span>

              @if (request()?.details?.bsn) {
              <span class="text-gray-600">BSN:</span>
              <span class="font-medium">{{ request()?.details?.bsn }}</span>
              }
            </div>
            @if(request()?.type === 'withdrawal' && inkoopkans()) {
            <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
              <span class="text-gray-600">Inkoop afspraak:</span>
              @if(inkoopAfspraak()) {
              <span class="font-medium">{{
                inkoopAfspraak()?.start_datetime | date : 'dd-MM-yyyy HH:mm'
              }}</span>
              } @else {
              <span class="font-medium">Geen inkoop afspraak</span>
              }
            </div>
            }
          </div>
        </ui-card>

        <ui-card class="h-full" [title]="'Consulent'" [loading]="loading()">
          <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
            <span class="text-gray-600">Naam:</span>
            <span class="font-medium"
              >{{ request()?.details?.consultant_first_name }}
              {{ request()?.details?.consultant_last_name }}</span
            >

            <span class="text-gray-600">Email:</span>
            <span class="font-medium">{{
              request()?.details?.consultant_email
            }}</span>

            <span class="text-gray-600">Municipality:</span>
            <span class="font-medium">{{
              request()?.details?.municipality
            }}</span>
          </div>
        </ui-card>

        @if (request()?.id) {
        <app-requests-documents
          [canUpload]="!isClosed()"
          [requestId]="request()?.id || null"
        ></app-requests-documents>
        } @if(deal()) {
        <app-requests-deals
          [deal]="deal()"
          [requestId]="request()?.id || null"
        ></app-requests-deals>
        }

        <!-- Mobile/Tablet: Timeline at the bottom -->
        <div class="lg:hidden">
          <ui-card [title]="'Tijdlijn'" [loading]="loadingEvents()">
            <div class="flex flex-col gap-2">
              <ui-timeline [items]="eventItems()"> </ui-timeline>
            </div>
          </ui-card>
        </div>
      </div>

      <!-- Desktop: Timeline on the right -->
      <div class="hidden lg:block">
        <ui-card [title]="'Tijdlijn'" [loading]="loadingEvents()">
          <div class="flex flex-col gap-2">
            <ui-timeline [items]="eventItems()"> </ui-timeline>
          </div>
        </ui-card>
      </div>
    </div>
  </div>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestsDetailComponent implements OnInit {
  // Injected services
  private readonly requestsService = inject(RequestsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly listPath =
    (this.route.snapshot.data['listPath'] as string | undefined) ?? '/requests';
  private readonly loadingService = inject(LoadingService);
  private readonly modalService = inject(ModalService);
  private readonly confirmService = inject(ConfirmService);
  private readonly appService = inject(AppService);
  // Loading states
  loading = signal(true);
  loadingEvents = signal(true);

  // Data signals
  request = signal<RequestDetail | null>(null);
  events = signal<RequestEvent[]>([]);
  eventItems = signal<TimelineItem[]>([]);
  deal = signal<RequestDeal | null>(null);
  inkoopkans = signal<Inkoopkans | null>(null);
  inkoopAfspraak = signal<any | null>(null);

  // Type mapping
  typeLabels = {
    withdrawal: 'Terugname',
    request: 'Aanvraag',
  };

  // Computed properties
  isClosed = computed(() => {
    const statuses = this.request()?.statuses;
    console.log(statuses);
    if (!statuses?.length) return false;

    const lastStatus = statuses[statuses.length - 1];
    return lastStatus.status.toLowerCase().includes('gesloten');
  });

  hasAssignment = computed(() => {
    return this.request()?.assignment !== null;
  });

  canEditRequest = computed(() => {
    // Controleer eerst of de gebruiker bewerkingsrechten heeft
    const hasEditRights = this.appService.isAdmin() || this.appService.isKeyUser() || this.appService.isConsulent();

    // Dan controleer dat de aanvraag nog niet gesloten is
    return hasEditRights && !this.isClosed();
  });

  canEditDeal = computed(() => {
    // Controleer eerst of de gebruiker bewerkingsrechten heeft
    const hasEditRights = this.appService.isAdmin() || this.appService.isKeyUser() || this.appService.isConsulent();

    // Dan controleer de bestaande voorwaarden
    return hasEditRights && this.isClosed() && !!this.request()?.zoho_verkoopkans_id;
  });

  isWaitingForSync = computed(() => {
    return this.isClosed() && !this.request()?.zoho_verkoopkans_id;
  });

  // Computed properties voor referenties (nu direct uit request.details)
  soReference = computed(() => {
    return this.request()?.details?.wmo_so_reference || '';
  });

  factuurReference = computed(() => {
    return this.request()?.details?.factuurreferentie || '';
  });

  constructor() {
    // effect(() => {
    //   if (this.appService.municipalityId()) {
    //     this.getRequest();
    //   }
    // }, { allowSignalWrites: true });
  }

  ngOnInit() {
    this.loadingService.show();
    this.getRequest();
  }

  getRequest() {
    this.loading.set(true);

    this.requestsService
      .getRequest(this.route.snapshot.params['id'])
      .subscribe({
        next: (request) => {
          this.request.set(request);
          this.loadingService.hide();
          this.loading.set(false);

          if (!this.appService.municipalityId() && request.municipality_id) {
            this.appService.setCurrentMunicipalityId(request.municipality_id);
          }

          this.getEventItems(request);
          this.getDeals();
          if (request.type === 'withdrawal') {
            this.getInkoopkans();
          }
        },
        error: (error) => {
          // Error handling is now done automatically by the ErrorInterceptor
          // The loading state will be reset and error message shown
          this.loading.set(false);
        }
      });
  }

  getEventItems(request: RequestDetail) {
    let items: TimelineItem[] = [];

    request.statuses.forEach((status) => {
      items.push({
        title: status.status.charAt(0).toUpperCase() + status.status.slice(1),
        date: status.created_at,
        content: '',
        badges: [],
      });
    });

    this.eventItems.set(items);
    this.loadingEvents.set(false);
  }

  getInkoopkans() {
    this.requestsService
      .getInkoopkans(this.route.snapshot.params['id'])
      .subscribe({
        next: (inkoopkans) => {
          this.inkoopkans.set(inkoopkans);
          const afspraak = inkoopkans.activities.find((activity: any) => activity.activity_type === 'Events');
          console.log(afspraak);
          if (afspraak) {
            this.inkoopAfspraak.set(afspraak);
          }
        },
        error: (error) => {
          // Error handling is now done automatically by the ErrorInterceptor
        }
      });
  }



  getDeals() {
    this.requestsService
      .getRequestDeal(this.route.snapshot.params['id'])
      .subscribe({
        next: (deal) => {
          console.log(deal);
          this.deal.set(deal);
        },
        error: (error) => {
          // Error handling is now done automatically by the ErrorInterceptor
        }
      });
  }

  getTypeLabel(type: string | undefined): string {
    if (!type) return '';
    return this.typeLabels[type as keyof typeof this.typeLabels] || type;
  }

  editRequest() {
    const modalRef = this.modalService.open(CreateRequestModalComponent, {
      data: {
        existingRequest: this.request(),
        isReadOnly: this.isClosed(),
      },
    });

    // Zet de isReadOnly property direct op de component instance
    if (modalRef.componentRef?.instance) {
      (modalRef.componentRef.instance as any).isReadOnly = this.isClosed();
    }

    modalRef.onClose.subscribe(() => {
      this.getRequest();
    });
  }

  editDeal() {
    const modalRef = this.modalService.open(EditDealModalComponent, {
      data: {
        request: this.request(),
      },
    });

    modalRef.onClose.subscribe(() => {
      this.getRequest();
      this.getDeals(); // Verfris ook de deal data
    });
  }

  getBadges(event: RequestEvent) {
    if (new Date(event.from) > new Date()) {
      return [{ label: 'Gepland', color: 'bg-primary-200' }];
    }
    return [];
  }

  goToAssignment() {
    if (this.request()?.assignment?.id) {
      this.router.navigate(['/assignments', this.request()?.assignment?.id]);
    }
  }

  closeRequest() {
    const requestId = this.request()?.id;
    if (!requestId) return;

    this.confirmService.confirm({
      title: 'Aanvraag afronden',
      message: 'Weet u zeker dat u deze aanvraag wilt afronden? Deze actie kan niet ongedaan worden gemaakt.',
      confirmText: 'Ja, afronden',
      cancelText: 'Annuleren',
      variant: 'success'
    }).then((confirmed) => {
      if (confirmed) {
        this.requestsService.completeConsulent(requestId).subscribe(() => {
          this.getRequest();
        });
      }
    });
  }

  cancelRequest() {
    const requestId = this.request()?.id;
    if (!requestId) return;

    this.confirmService.confirm({
      title: 'Aanvraag annuleren',
      message: 'Weet u zeker dat u deze aanvraag wilt annuleren? Deze actie kan niet ongedaan worden gemaakt.',
      confirmText: 'Ja, annuleren',
      cancelText: 'Nee, behouden'
    }).then((confirmed) => {
      if (confirmed) {
        this.requestsService.cancelRequest(requestId).subscribe(() => {
          this.getRequest();
        });
      }
    });
  }
}
